// src/components/coordinateGrid/GridRenderer.tsx
/**
 * GridRenderer Component
 * 
 * Renders the background grid for the 2D coordinate system.
 * Supports multiple styles (lines, dots) and density levels.
 * 
 * Phase 1: Core infrastructure
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import {
  GridStyle,
  GridDensity,
  QuadrantVisibility,
  getGridInterval,
  getMinorGridInterval,
} from '../../types/coordinateGrid';

interface GridRendererProps {
  /** SVG width in pixels */
  width: number;
  /** SVG height in pixels */
  height: number;
  /** Pixels per unit on x-axis */
  scaleX: number;
  /** Pixels per unit on y-axis */
  scaleY: number;
  /** X offset (position of origin in pixels from left) */
  offsetX: number;
  /** Y offset (position of origin in pixels from top) */
  offsetY: number;
  /** Override grid style */
  gridStyle?: GridStyle;
  /** Override grid density */
  gridDensity?: GridDensity;
  /** Override grid color */
  gridColor?: string;
  /** Override minor grid color */
  minorGridColor?: string;
  /** Override show minor gridlines */
  showMinorGridlines?: boolean;
  /** Override quadrant visibility */
  quadrantsVisible?: QuadrantVisibility;
  /** Whether dark mode is active */
  isDark?: boolean;
}

/**
 * Generate array of grid values within a range
 */
function generateGridValues(
  start: number,
  end: number,
  interval: number
): number[] {
  const values: number[] = [];
  // Start from a multiple of interval
  const startValue = Math.ceil(start / interval) * interval;
  
  for (let value = startValue; value <= end; value += interval) {
    // Round to avoid floating point errors
    const roundedValue = Math.round(value * 1000) / 1000;
    values.push(roundedValue);
  }
  
  return values;
}

/**
 * Check if a position is within visible quadrants
 */
function isInVisibleQuadrant(
  x: number,
  y: number,
  quadrants: QuadrantVisibility
): boolean {
  const { q1, q2, q3, q4 } = quadrants;
  
  // Q1: +x, +y (top-right in screen coords means positive x, positive y in math coords)
  // Note: In SVG, y increases downward, so we need to flip the logic
  // When y < offsetY (above origin line), it's positive y in math
  // When x > offsetX (right of origin), it's positive x in math
  
  if (x >= 0 && y >= 0) return q1; // Math Q1
  if (x < 0 && y >= 0) return q2;  // Math Q2
  if (x < 0 && y < 0) return q3;   // Math Q3
  if (x >= 0 && y < 0) return q4;  // Math Q4
  
  return true;
}

export const GridRenderer: React.FC<GridRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  gridStyle: gridStyleProp,
  gridDensity: gridDensityProp,
  gridColor: gridColorProp,
  minorGridColor: minorGridColorProp,
  showMinorGridlines: showMinorGridlinesProp,
  quadrantsVisible: quadrantsVisibleProp,
  isDark = false,
}) => {
  // Get settings from Redux if not overridden by props
  const coordinateGrid = useSelector((state: RootState) => state.coordinateGrid);
  
  const gridStyle = gridStyleProp ?? coordinateGrid.gridStyle;
  const gridDensity = gridDensityProp ?? coordinateGrid.gridDensity;
  const gridColor = gridColorProp ?? (isDark ? '#374151' : coordinateGrid.gridColor);
  const minorGridColor = minorGridColorProp ?? (isDark ? '#1F2937' : coordinateGrid.minorGridColor);
  const showMinorGridlines = showMinorGridlinesProp ?? coordinateGrid.showMinorGridlines;
  const quadrantsVisible = quadrantsVisibleProp ?? coordinateGrid.quadrantsVisible;

  // Calculate visible domain in math coordinates
  const domain = useMemo(() => {
    const xMin = -offsetX / scaleX;
    const xMax = (width - offsetX) / scaleX;
    const yMin = -(height - offsetY) / scaleY; // Note: y is flipped in SVG
    const yMax = offsetY / scaleY;
    
    return { xMin, xMax, yMin, yMax };
  }, [width, height, scaleX, scaleY, offsetX, offsetY]);

  // Generate grid values
  const gridInterval = getGridInterval(gridDensity);
  const minorInterval = getMinorGridInterval(gridDensity);

  const { majorLines, minorLines, dots } = useMemo(() => {
    const majorXValues = generateGridValues(domain.xMin, domain.xMax, gridInterval);
    const majorYValues = generateGridValues(domain.yMin, domain.yMax, gridInterval);
    
    const minorXValues = showMinorGridlines
      ? generateGridValues(domain.xMin, domain.xMax, minorInterval).filter(
          v => !majorXValues.includes(v)
        )
      : [];
    const minorYValues = showMinorGridlines
      ? generateGridValues(domain.yMin, domain.yMax, minorInterval).filter(
          v => !majorYValues.includes(v)
        )
      : [];

    // Convert to pixel positions
    const toPixelX = (value: number) => offsetX + value * scaleX;
    const toPixelY = (value: number) => offsetY - value * scaleY; // Flip Y

    // Generate line data
    const majorLineData: { x1: number; y1: number; x2: number; y2: number; isVertical: boolean }[] = [];
    const minorLineData: { x1: number; y1: number; x2: number; y2: number; isVertical: boolean }[] = [];
    const dotData: { cx: number; cy: number; isMajor: boolean }[] = [];

    // Vertical major lines
    majorXValues.forEach(x => {
      const px = toPixelX(x);
      if (px >= 0 && px <= width) {
        majorLineData.push({
          x1: px,
          y1: 0,
          x2: px,
          y2: height,
          isVertical: true,
        });
      }
    });

    // Horizontal major lines
    majorYValues.forEach(y => {
      const py = toPixelY(y);
      if (py >= 0 && py <= height) {
        majorLineData.push({
          x1: 0,
          y1: py,
          x2: width,
          y2: py,
          isVertical: false,
        });
      }
    });

    // Vertical minor lines
    minorXValues.forEach(x => {
      const px = toPixelX(x);
      if (px >= 0 && px <= width) {
        minorLineData.push({
          x1: px,
          y1: 0,
          x2: px,
          y2: height,
          isVertical: true,
        });
      }
    });

    // Horizontal minor lines
    minorYValues.forEach(y => {
      const py = toPixelY(y);
      if (py >= 0 && py <= height) {
        minorLineData.push({
          x1: 0,
          y1: py,
          x2: width,
          y2: py,
          isVertical: false,
        });
      }
    });

    // Generate dots at intersections (for dot style)
    if (gridStyle === 'dots') {
      majorXValues.forEach(x => {
        majorYValues.forEach(y => {
          if (isInVisibleQuadrant(x, y, quadrantsVisible)) {
            const px = toPixelX(x);
            const py = toPixelY(y);
            if (px >= 0 && px <= width && py >= 0 && py <= height) {
              dotData.push({ cx: px, cy: py, isMajor: true });
            }
          }
        });
      });

      if (showMinorGridlines) {
        // Add minor dots
        const allXValues = [...majorXValues, ...minorXValues];
        const allYValues = [...majorYValues, ...minorYValues];
        
        allXValues.forEach(x => {
          allYValues.forEach(y => {
            // Skip if it's a major intersection
            if (majorXValues.includes(x) && majorYValues.includes(y)) return;
            
            if (isInVisibleQuadrant(x, y, quadrantsVisible)) {
              const px = toPixelX(x);
              const py = toPixelY(y);
              if (px >= 0 && px <= width && py >= 0 && py <= height) {
                dotData.push({ cx: px, cy: py, isMajor: false });
              }
            }
          });
        });
      }
    }

    return {
      majorLines: majorLineData,
      minorLines: minorLineData,
      dots: dotData,
    };
  }, [domain, gridInterval, minorInterval, scaleX, scaleY, offsetX, offsetY, 
      width, height, gridStyle, showMinorGridlines, quadrantsVisible]);

  // Don't render if grid style is none
  if (gridStyle === 'none') {
    return null;
  }

  // Render dots style
  if (gridStyle === 'dots') {
    return (
      <g className="grid-layer grid-dots">
        {dots.map((dot, index) => (
          <circle
            key={`dot-${index}`}
            cx={dot.cx}
            cy={dot.cy}
            r={dot.isMajor ? 2 : 1}
            fill={dot.isMajor ? gridColor : minorGridColor}
            className="grid-dot"
          />
        ))}
      </g>
    );
  }

  // Render lines style (default)
  return (
    <g className="grid-layer grid-lines">
      {/* Minor gridlines (render first, behind major) */}
      {showMinorGridlines && minorLines.map((line, index) => (
        <line
          key={`minor-${index}`}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={minorGridColor}
          strokeWidth={0.5}
          className="grid-line grid-line-minor"
        />
      ))}
      
      {/* Major gridlines */}
      {majorLines.map((line, index) => (
        <line
          key={`major-${index}`}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={gridColor}
          strokeWidth={1}
          className="grid-line grid-line-major"
        />
      ))}
    </g>
  );
};

export default GridRenderer;
